import { Add, Cancel, CheckCircle, Delete, Edit, LockOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Stack,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  AdminDialog,
  AdminEmptyState,
  AdminFilterPanel,
  AdminTableActions,
  AdminTableBodyState,
  AdminTablePanel,
  AppChip,
  ErrorState,
  PageShell,
} from "@/components/common";
import { LocalizedTablePagination } from "@/components/common/LocalizedTablePagination";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { RecordFields, RecordList } from "@/components/common/RecordList";
import { RecordSortControl } from "@/components/common/RecordSortControl";
import { SortableTableCell } from "@/components/common/SortableTableCell";
import { useTableSorting } from "@/hooks/useTableSorting";
import { paths } from "@/routes/paths";
import { Customer, CustomerService } from "@/services/features/CustomerService";
import {
  CustomerSsoProvider,
  platformsOf,
  SSO_PLATFORM_LABELS,
  SSO_PROTOCOL_LABELS,
  SsoProtocol,
  SsoProviderService,
} from "@/services/features/SsoProviderService";

const protocolLogos: Record<SsoProtocol, string> = {
  OIDC: "/images/protocols/openid.svg",
  SAML: "/images/protocols/saml.svg",
};

type LoadStatus = "loading" | "error" | "ready";
type ProviderColumn = "customer" | "name" | "protocol" | "platforms" | "status";
const columns: ProviderColumn[] = ["status", "customer", "name", "protocol", "platforms"];
const platformValue = (provider: CustomerSsoProvider) =>
  platformsOf(provider)
    .map((platform) => SSO_PLATFORM_LABELS[platform])
    .join(" · ");

export default function SsoProviders() {
  const { t, i18n } = useTranslation();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customersStatus, setCustomersStatus] = useState<LoadStatus>("loading");
  const [providers, setProviders] = useState<CustomerSsoProvider[]>([]);
  const [providersStatus, setProvidersStatus] = useState<LoadStatus>("loading");
  const [revision, setRevision] = useState(0);
  const [deleting, setDeleting] = useState<CustomerSsoProvider | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // Both lists load together: the overview shows every provider until a customer is picked as a filter.
  useEffect(() => {
    let active = true;
    setCustomersStatus("loading");
    setProvidersStatus("loading");
    const loadCustomers = async () => {
      try {
        const result = await CustomerService.getInstance().getAllCustomers();
        if (!result.success || !result.data) throw new Error("Unable to load customers");
        if (active) {
          setCustomers(result.data.toSorted((a, b) => a.name.localeCompare(b.name)));
          setCustomersStatus("ready");
        }
      } catch {
        if (active) setCustomersStatus("error");
      }
    };
    const loadProviders = async () => {
      try {
        const result = await SsoProviderService.getInstance().getAllProviders();
        if (active) {
          setProviders(result);
          setProvidersStatus("ready");
        }
      } catch {
        if (active) setProvidersStatus("error");
      }
    };
    void loadCustomers();
    void loadProviders();
    return () => {
      active = false;
    };
  }, [revision]);

  const customer = customers.find((entry) => entry.id === Number(params.get("customerId"))) ?? null;
  const customerName = (customerId: number) =>
    customers.find((entry) => entry.id === customerId)?.name ?? `#${customerId}`;
  const visibleProviders = customer
    ? providers.filter((provider) => provider.customerId === customer.id)
    : providers;

  const { sortColumn, sortDirection, handleSort, sortedItems } = useTableSorting(
    visibleProviders,
    (provider, column: ProviderColumn) => {
      switch (column) {
        case "customer":
          // Keep each customer's providers together, in name order, when sorting by customer.
          return `${customerName(provider.customerId)}\u0000${provider.name}`;
        case "name":
          return provider.name;
        case "protocol":
          return SSO_PROTOCOL_LABELS[provider.protocol] ?? provider.protocol;
        case "platforms":
          return platformValue(provider);
        case "status":
          return t(provider.enabled ? "management.sso.enabled" : "management.sso.disabled");
      }
    },
    "customer",
    i18n.resolvedLanguage ?? i18n.language,
  );

  const [recordPage, setRecordPage] = useState(0);
  const [recordPageSize, setRecordPageSize] = useState(25);
  const currentRecordPage = Math.min(
    recordPage,
    Math.max(0, Math.ceil(sortedItems.length / recordPageSize) - 1),
  );
  const displayedRecords = sortedItems.slice(
    currentRecordPage * recordPageSize,
    (currentRecordPage + 1) * recordPageSize,
  );
  useEffect(() => {
    setRecordPage(0);
  }, [sortColumn, sortDirection, customer?.id]);
  const renderRecordPagination = () => (
    <LocalizedTablePagination
      component="div"
      count={sortedItems.length}
      page={currentRecordPage}
      rowsPerPage={recordPageSize}
      rowsPerPageOptions={[25, 50, 100]}
      onPageChange={(_, next) => setRecordPage(next)}
      onRowsPerPageChange={(event) => {
        setRecordPageSize(Number(event.target.value));
        setRecordPage(0);
      }}
    />
  );

  const retry = () => setRevision((value) => value + 1);
  const editorPath = (provider: CustomerSsoProvider) =>
    `${paths.admin.sso}/${provider.customerId}/${provider.id}`;

  const deleteProvider = async () => {
    if (!deleting || busy) return;
    setBusy(true);
    setDeleteError("");
    try {
      await SsoProviderService.getInstance().deleteProvider(deleting.customerId, deleting.id);
      setProviders((current) => current.filter((entry) => entry.id !== deleting.id));
      setDeleting(null);
      enqueueSnackbar(t("management.sso.deleted"), { variant: "success" });
    } catch {
      setDeleteError(t("management.sso.errors.delete"));
    } finally {
      setBusy(false);
    }
  };

  const failed = customersStatus === "error" || providersStatus === "error";
  const emptyState = failed ? (
    <ErrorState
      title={t(
        customersStatus === "error"
          ? "management.sso.errors.customers"
          : "management.sso.errors.providers",
      )}
      onRetry={retry}
    />
  ) : (
    <AdminEmptyState
      icon={<LockOutlined sx={{ fontSize: 40 }} />}
      text={t("management.sso.empty.noProviders")}
      description={t(
        customer ? "management.sso.empty.addProvider" : "management.sso.empty.selectToAdd",
      )}
    />
  );

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("management.sso.title")}
      icon={<LockOutlined />}
      headerActions={
        <Button
          variant="contained"
          startIcon={<Add />}
          disabled={!customer || customersStatus !== "ready"}
          onClick={() => {
            if (customer) void navigate(`${paths.admin.sso}/${customer.id}/new`);
          }}
        >
          {t("management.sso.actions.addProvider")}
        </Button>
      }
      contextControls={
        <AdminFilterPanel>
          <MuiAutocompleteMultiSelect
            multiSelect={false}
            label={t("common.customer")}
            placeholder={t("management.sso.empty.selectCustomer")}
            options={customers}
            value={customer ? [customer] : []}
            getOptionLabel={(option) => option.name}
            getOptionKey={(option) => option.id}
            onChange={(next) => setParams(next[0] ? { customerId: String(next[0].id) } : {})}
            loading={customersStatus === "loading"}
            disabled={customersStatus !== "ready"}
          />
        </AdminFilterPanel>
      }
    >
      <AdminTablePanel
        header={renderRecordPagination()}
        footer={renderRecordPagination()}
        tableSx={{
          "& .MuiTableHead-root .MuiTableCell-root": {
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          },
          "& .MuiTableHead-root .MuiTableCell-root:first-of-type": {
            borderInlineStart: (theme) => `1px solid ${theme.palette.divider}`,
          },
          "& .MuiTableHead-root .MuiTableCell-root:last-of-type": {
            borderInlineEnd: (theme) => `1px solid ${theme.palette.divider}`,
          },
        }}

        mobileContent={
          <Box
            sx={{
              minWidth: 0,
              "& .MuiTypography-root": {
                maxWidth: "100%",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
              },
            }}
          >
            <RecordSortControl
              value={sortColumn}
              direction={sortDirection}
              onSort={handleSort}
              options={columns.map((column) => ({
                value: column,
                label: t(`management.sso.columns.${column}`),
              }))}
            />
            <RecordList
              items={failed ? [] : displayedRecords}
              getItemId={(provider) => provider.id}
              loading={!failed && (customersStatus === "loading" || providersStatus === "loading")}
              resetKey={JSON.stringify([
                sortColumn,
                sortDirection,
                ...(failed ? [] : displayedRecords).map((provider) => provider.id),
              ])}
              renderTitle={(provider) => {
                return <>{provider.name}</>;
              }}
              renderSummary={(provider) => {
                return (
                  <RecordFields
                    fields={[
                      {
                        label: t("management.sso.columns.customer"),
                        value: customerName(provider.customerId),
                      },
                      {
                        label: <>{t("management.sso.columns.status")}</>,
                        value: (
                          <>
                            <Tooltip
                              title={t(
                                provider.enabled
                                  ? "management.sso.enabled"
                                  : "management.sso.disabled",
                              )}
                            >
                              <Box
                                component="span"
                                role="img"
                                aria-label={t(
                                  provider.enabled
                                    ? "management.sso.enabled"
                                    : "management.sso.disabled",
                                )}
                                sx={{ display: "inline-flex" }}
                              >
                                {provider.enabled ? (
                                  <CheckCircle color="success" sx={{ fontSize: 18 }} />
                                ) : (
                                  <Cancel color="error" sx={{ fontSize: 18 }} />
                                )}
                              </Box>
                            </Tooltip>
                          </>
                        ),
                      },
                      {
                        label: <>{t("management.sso.columns.protocol")}</>,
                        value: (
                          <>
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                              {protocolLogos[provider.protocol] && (
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 32,
                                    height: 32,
                                    borderRadius: 1,
                                    bgcolor: (theme) =>
                                      theme.palette.mode === "dark"
                                        ? "common.white"
                                        : "transparent",
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={protocolLogos[provider.protocol]}
                                    alt=""
                                    aria-hidden="true"
                                    sx={{
                                      display: "block",
                                      height: 24,
                                      width: 24,
                                      objectFit: "contain",
                                    }}
                                  />
                                </Box>
                              )}
                              <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                                {SSO_PROTOCOL_LABELS[provider.protocol] ?? provider.protocol}
                              </Typography>
                            </Box>
                          </>
                        ),
                      },
                    ]}
                  />
                );
              }}
              renderDetails={(provider) => {
                return (
                  <RecordFields
                    fields={[
                      {
                        label: <>{t("management.sso.columns.platforms")}</>,
                        value: (
                          <>
                            <Stack spacing={0.5} alignItems="flex-start">
                              {platformsOf(provider).map((platform) => (
                                <AppChip
                                  key={platform}
                                  tone="neutral"
                                  size="small"
                                  label={SSO_PLATFORM_LABELS[platform]}
                                />
                              ))}
                            </Stack>
                          </>
                        ),
                      },
                    ]}
                  />
                );
              }}
              renderActions={(provider) => (
                <AdminTableActions
                  actions={[
                    {
                      icon: <Edit />,
                      hint: t("management.sso.actions.editProvider"),
                      ariaLabel: t("management.sso.actions.editNamed", {
                        name: provider.name,
                      }),
                      action: () => void navigate(editorPath(provider)),
                    },
                    {
                      icon: <Delete />,
                      hint: t("management.sso.actions.deleteProvider"),
                      ariaLabel: t("management.sso.actions.deleteNamed", {
                        name: provider.name,
                      }),
                      color: "error",
                      action: () => {
                        setDeleteError("");
                        setDeleting(provider);
                      },
                    },
                  ]}
                />
              )}
              emptyState={emptyState}
            />
          </Box>
        }
      >
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <SortableTableCell
                key={column}
                column={column}
                activeColumn={sortColumn}
                direction={sortDirection}
                onSort={handleSort}
                sx={{ width: column === "protocol" ? "100%" : "1%", whiteSpace: "nowrap" }}
              >
                {t(`management.sso.columns.${column}`)}
              </SortableTableCell>
            ))}
            <TableCell sx={{ width: "1%", whiteSpace: "nowrap", textAlign: "end" }}>
              {t("management.sso.columns.actions")}
            </TableCell>
          </TableRow>
        </TableHead>
        <AdminTableBodyState
          items={failed ? [] : displayedRecords}
          isLoading={!failed && (customersStatus === "loading" || providersStatus === "loading")}
          colSpan={columns.length + 1}
          loadingLabel={t(
            customersStatus === "loading"
              ? "management.sso.loading.customers"
              : "management.sso.loading.providers",
          )}
          emptyState={emptyState}
        >
          {(provider) => (
            <TableRow
              key={provider.id}
              hover
              tabIndex={0}
              onClick={() => void navigate(editorPath(provider))}
              onKeyDown={(event) => {
                if (event.target !== event.currentTarget) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  void navigate(editorPath(provider));
                }
              }}
              sx={{
                cursor: "pointer",
                "&:focus-visible": {
                  outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: -2,
                },
              }}
            >
              <TableCell align="center">
                <Tooltip
                  title={t(provider.enabled ? "management.sso.enabled" : "management.sso.disabled")}
                >
                  <Box
                    component="span"
                    role="img"
                    aria-label={t(
                      provider.enabled ? "management.sso.enabled" : "management.sso.disabled",
                    )}
                    sx={{ display: "inline-flex" }}
                  >
                    {provider.enabled ? (
                      <CheckCircle color="success" sx={{ fontSize: 18 }} />
                    ) : (
                      <Cancel color="error" sx={{ fontSize: 18 }} />
                    )}
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                {customerName(provider.customerId)}
              </TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                  {provider.iconUrl && (
                    <Box
                      component="img"
                      src={provider.iconUrl}
                      alt=""
                      aria-hidden="true"
                      sx={{ display: "block", width: 20, height: 20 }}
                    />
                  )}
                  {provider.name}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                  {protocolLogos[provider.protocol] && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark" ? "common.white" : "transparent",
                      }}
                    >
                      <Box
                        component="img"
                        src={protocolLogos[provider.protocol]}
                        alt=""
                        aria-hidden="true"
                        sx={{
                          display: "block",
                          height: 24,
                          width: 24,
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  )}
                  <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                    {SSO_PROTOCOL_LABELS[provider.protocol] ?? provider.protocol}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Stack spacing={0.5} alignItems="flex-start">
                  {platformsOf(provider).map((platform) => (
                    <AppChip
                      key={platform}
                      tone="neutral"
                      size="small"
                      label={SSO_PLATFORM_LABELS[platform]}
                    />
                  ))}
                </Stack>
              </TableCell>
              <TableCell>
                <AdminTableActions
                  actions={[
                    {
                      icon: <Edit />,
                      hint: t("management.sso.actions.editProvider"),
                      ariaLabel: t("management.sso.actions.editNamed", { name: provider.name }),
                      action: () => void navigate(editorPath(provider)),
                    },
                    {
                      icon: <Delete />,
                      hint: t("management.sso.actions.deleteProvider"),
                      ariaLabel: t("management.sso.actions.deleteNamed", { name: provider.name }),
                      color: "error",
                      action: () => {
                        setDeleteError("");
                        setDeleting(provider);
                      },
                    },
                  ]}
                />
              </TableCell>
            </TableRow>
          )}
        </AdminTableBodyState>
      </AdminTablePanel>
      <AdminDialog
        open={deleting !== null}
        title={t("management.sso.deleteTitle")}
        onClose={() => {
          if (!busy) setDeleting(null);
        }}
        error={deleteError}
        cancelAction={{
          label: t("common.cancel"),
          onClick: () => setDeleting(null),
          disabled: busy,
        }}
        primaryAction={{
          label: t("management.sso.actions.deleteProvider"),
          color: "error",
          onClick: () => void deleteProvider(),
          loading: busy,
        }}
      >
        <Typography>
          {t("management.sso.confirmDelete", {
            name: deleting?.name ?? "",
            customer: deleting ? customerName(deleting.customerId) : "",
          })}
        </Typography>
      </AdminDialog>
    </PageShell>
  );
}
